import { useState } from 'react';
import type { Movimiento } from '../../lib/supabase';
import type { ComprobanteResumen, EstadoComprobante, Respaldo } from '../../types/comprobante';
import { estaObjetado, estadoComprobanteDelPago, estadoComprobanteDelReembolso } from '../../utils/comprobantePlazo';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { EstadoComprobanteBadge } from './EstadoComprobanteBadge';
import { ComprobanteVisor } from './ComprobanteVisor';
import { abrirPdfEnPestanaNueva } from './abrirComprobante';

interface ComprobantesDeMovimientoProps {
  readonly movimiento: Movimiento;
  readonly ahora: Date;
  /** Sin esto la celda es de solo lectura (vistas del nivel superior). */
  readonly onAdjuntar?: (movimiento: Movimiento, respaldo: Respaldo, modo: 'adjuntar' | 'reemplazar') => void;
  readonly onReembolsar?: (movimiento: Movimiento) => void;
}

export const ComprobantesDeMovimiento: React.FC<ComprobantesDeMovimientoProps> = ({ movimiento, ahora, onAdjuntar, onReembolsar }) => {
  const [visor, setVisor] = useState<{ comprobante: ComprobanteResumen; respaldo: Respaldo } | null>(null);
  const estadoPago = estadoComprobanteDelPago(movimiento, ahora);
  const estadoReembolso = estadoComprobanteDelReembolso(movimiento, ahora);

  const ver = (respaldo: Respaldo) => (comprobante: ComprobanteResumen) => {
    if (comprobante.tipo_archivo === 'pdf') {
      abrirPdfEnPestanaNueva(comprobante).catch((e: unknown) => { reportarErrorInesperado(e, 'No pudimos abrir el comprobante'); });
      return;
    }
    setVisor({ comprobante, respaldo });
  };

  // Un comprobante objetado se reemplaza: el botón dice "Reemplazar comprobante" y la ventana se abre
  // en ese modo, directamente sobre este movimiento.
  const adjuntarA = (respaldo: Respaldo, estado: EstadoComprobante) => {
    if (!onAdjuntar) return undefined;
    return () => { onAdjuntar(movimiento, respaldo, estaObjetado(estado) ? 'reemplazar' : 'adjuntar'); };
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <EstadoComprobanteBadge estado={estadoPago} ahora={ahora} onVer={ver('pago')} onAdjuntar={adjuntarA('pago', estadoPago)} />
      {estadoReembolso && (
        <EstadoComprobanteBadge estado={estadoReembolso} ahora={ahora} etiqueta="Reembolso"
          onVer={ver('reembolso')} onAdjuntar={adjuntarA('reembolso', estadoReembolso)} />
      )}
      {onReembolsar && movimiento.tipo === 'egreso' && !movimiento.anulado
        && movimiento.requiere_reembolso && !movimiento.reembolsado
        && (movimiento.origen_tipo === 'actividad' || (movimiento.origen_tipo == null && movimiento.origen_id == null)) && (
        <button type="button" className="min-h-11 text-sm font-medium text-blue-600 hover:underline"
          onClick={() => onReembolsar(movimiento)}>Marcar reembolsado</button>
      )}
      {visor && (
        <ComprobanteVisor
          comprobante={visor.comprobante}
          historial={{ movimientoId: movimiento.id, respaldo: visor.respaldo }}
          onClose={() => { setVisor(null); }}
          onReemplazar={onAdjuntar ? () => { setVisor(null); onAdjuntar(movimiento, visor.respaldo, 'reemplazar'); } : undefined}
        />
      )}
    </div>
  );
};
